import { TabConfig } from '@energy-stacks/core/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChargingStationDetailsGeneralTab } from './protocol-v1.6/ChargingStationDetailsGeneralTab';
import { ChargingStationDetailsConfigurationTab } from './protocol-v1.6/configuration-tab/ChargingStationDetailsConfigurationTab';
import { HardResetRequiredBanner } from './protocol-v1.6/configuration-tab/HardResetRequiredBanner';
import { ChargingStationDetailsLogsTab } from './protocol-v1.6/logs/ChargingStationDetailsLogsTab';
import { ChargingStationDetailsTransactionsTab } from './protocol-v1.6/transactions/ChargingStationDetailsTransactionsTab';
import { ChargingStationDetailsConfigurationTabV201 } from './protocol-v201/ChargingStationDetailsConfigurationTabV201';
import { ChargingStationDetailsGeneralTabV201 } from './protocol-v201/ChargingStationDetailsGeneralTabV201';
import { ChargingStationDetailsLogsTabV201 } from './protocol-v201/logs/ChargingStationDetailsLogsTabV201';
import { ChargingStationDetailsSecurityEventsTabV201 } from './protocol-v201/security-events/ChargingStationDetailsSecurityEventsTabV201';
import { ChargingStationDetailsGeneralTabV21 } from './protocol-v21/ChargingStationDetailsGeneralTabV21';
import { ChargingStationDetailsConfigurationTabV21 } from './protocol-v21/ChargingStationDetailsConfigurationTabV21';
import { ChargingStationDetailsTransactionsTabV21 } from './protocol-v21/transactions/ChargingStationDetailsTransactionsTabV21';
import { ChargingStationDetailsLogsTabV21 } from './protocol-v21/logs/ChargingStationDetailsLogsTabV21';
import { ChargingStationDetailsSecurityEventsTabV21 } from './protocol-v21/security-events/ChargingStationDetailsSecurityEventsTabV21';
import { BrokerRoutes } from '@energy-stacks/broker/shared';
import { OcppProtocol } from '@energy-stacks/shared';
import { ChargingStationDetailsTransactionsTabV201 } from './protocol-v201/transactions/ChargingStationDetailsTransactionsTabV201';
import { ChargingStationDetailsNetworkLogsTab } from './network-logs/ChargingStationDetailsNetworkLogsTab';
import { ChargingStationDetailsAlertsTab } from './protocol-v1.6/alerts/ChargingStationDetailsAlertsTab';
import { environment } from '@energy-stacks/feature-config';

export const useChargingStationDetailsTabs = (
  ocppProtocol: OcppProtocol | undefined
) => {
  const [t] = useTranslation('chargingStations');
  const [tTransactions] = useTranslation('transactions');

  const config: TabConfig = useMemo(() => {
    const tabRootPath = `${BrokerRoutes.ChargingStations}/${BrokerRoutes.ChargingStationDetails}`;

    if (ocppProtocol === 'V_201') {
      return {
        tabRootPath,
        tabs: [
          {
            component: ChargingStationDetailsGeneralTabV201,
            name: t('information'),
            path: BrokerRoutes.ChargingStationDetailsGeneralTabV201,
          },
          {
            component: ChargingStationDetailsConfigurationTabV201,
            name: t('configuration'),
            path: BrokerRoutes.ChargingStationDetailsConfigurationTabV201,
          },
          {
            component: ChargingStationDetailsTransactionsTabV201,
            name: tTransactions('transactions'),
            path: BrokerRoutes.ChargingStationDetailsTransactionsTabV201,
          },
          {
            component: ChargingStationDetailsLogsTabV201,
            name: t('logs'),
            path: BrokerRoutes.ChargingStationDetailsLogsTabV201,
          },
          {
            component: ChargingStationDetailsSecurityEventsTabV201,
            name: t('securityEvents'),
            path: BrokerRoutes.ChargingStationDetailsSecurityEventsTabV201,
          },
          {
            component: ChargingStationDetailsNetworkLogsTab,
            name: t('networkLogs'),
            path: BrokerRoutes.ChargingStationDetailsNetworkLogsTab,
          },
        ],
      };
    }

    if (ocppProtocol === 'V_21') {
      return {
        tabRootPath,
        tabs: [
          {
            component: ChargingStationDetailsGeneralTabV21,
            name: t('information'),
            path: BrokerRoutes.ChargingStationDetailsGeneralTabV21,
          },
          {
            component: ChargingStationDetailsConfigurationTabV21,
            name: t('configuration'),
            path: BrokerRoutes.ChargingStationDetailsConfigurationTabV21,
          },
          {
            component: ChargingStationDetailsTransactionsTabV21,
            name: tTransactions('transactions'),
            path: BrokerRoutes.ChargingStationDetailsTransactionsTabV21,
          },
          {
            component: ChargingStationDetailsLogsTabV21,
            name: t('logs'),
            path: BrokerRoutes.ChargingStationDetailsLogsTabV21,
          },
          {
            component: ChargingStationDetailsSecurityEventsTabV21,
            name: t('securityEvents'),
            path: BrokerRoutes.ChargingStationDetailsSecurityEventsTabV21,
          },
          {
            component: ChargingStationDetailsNetworkLogsTab,
            name: t('networkLogs'),
            path: BrokerRoutes.ChargingStationDetailsNetworkLogsTab,
          },
        ],
      };
    }

    return {
      tabRootPath,
      tabs: [
        {
          component: ChargingStationDetailsGeneralTab,
          name: t('general'),
          path: BrokerRoutes.ChargingStationDetailsGeneralTab,
        },
        {
          component: ChargingStationDetailsConfigurationTab,
          name: t('configuration'),
          path: BrokerRoutes.ChargingStationDetailsConfigurationTab,
          banner: HardResetRequiredBanner,
        },
        {
          component: ChargingStationDetailsTransactionsTab,
          name: tTransactions('transactions'),
          path: BrokerRoutes.ChargingStationDetailsTransactionsTab,
        },
        {
          component: ChargingStationDetailsLogsTab,
          name: t('logs'),
          path: BrokerRoutes.ChargingStationDetailsLogsTab,
        },
        {
          component: ChargingStationDetailsNetworkLogsTab,
          name: t('networkLogs'),
          path: BrokerRoutes.ChargingStationDetailsNetworkLogsTab,
        },
        ...(!environment.hideAlertManagement
          ? [
              {
                component: ChargingStationDetailsAlertsTab,
                name: t('alerts'),
                path: BrokerRoutes.ChargingStationDetailsAlertsTab,
              },
            ]
          : []),
      ],
    };
  }, [ocppProtocol, t, tTransactions]);

  return { config };
};
